import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const DATE_RANGE_CONFIG = {
  "7D": { type: "DAY", rangeValue: 7 },
  "14D": { type: "DAY", rangeValue: 14 },
  "21D": { type: "DAY", rangeValue: 21 },
};

const FeedingTimeline = ({
  feedingsData,
  activeFeeding,
  showDateRangeSelector = false,
}) => {
  const { t } = useTranslation();
  const dateRangeOptions = [
    { value: "7D", label: t("charts.1_week") },
    { value: "14D", label: t("charts.2_weeks") },
    { value: "21D", label: t("charts.3_weeks") },
  ];
  const [dateRange, setDateRange] = useState("7D");

  // Calculate average feeding patterns from the data
  const averageFeedingPatterns = useMemo(() => {
    if (!feedingsData || feedingsData.length === 0) return [];

    // Filter data based on selected date range
    const now = new Date();
    const cutoffDate = new Date();

    const selectedOption = DATE_RANGE_CONFIG[dateRange];
    if (selectedOption) {
      if (selectedOption.type === "DAY") {
        cutoffDate.setDate(now.getDate() - selectedOption.rangeValue);
      } else if (selectedOption.type === "MONTH") {
        cutoffDate.setMonth(now.getMonth() - selectedOption.rangeValue);
      }
    } else {
      cutoffDate.setMonth(now.getMonth() - 1); // default
    }

    const filteredFeedings = feedingsData.filter((feeding) => {
      const feedingDate = new Date(feeding.start);
      return feedingDate >= cutoffDate;
    });

    // Group feedings by their start time (rounded to nearest hour)
    const feedingsByHour = {};

    filteredFeedings.forEach((feeding) => {
      if (!feeding.end) return; // Skip active/incomplete feedings

      const startDate = new Date(feeding.start);

      // Get hour of day for start time
      const startHour = startDate.getHours();
      const startMinute = startDate.getMinutes();
      const startTimeInMinutes = startHour * 60 + startMinute;

      // Store feeding info
      if (!feedingsByHour[startHour]) {
        feedingsByHour[startHour] = [];
      }

      feedingsByHour[startHour].push({
        startTimeInMinutes,
        startHour,
        startMinute,
      });
    });

    // Calculate average feeding patterns by clustering similar feeding times
    const clusters = [];

    // Sort hours
    const hours = Object.keys(feedingsByHour)
      .map(Number)
      .sort((a, b) => a - b);

    hours.forEach((hour) => {
      const feedings = feedingsByHour[hour];

      // Calculate average start time for this hour
      const avgStartMinutes =
        feedings.reduce((sum, s) => sum + s.startTimeInMinutes, 0) /
        feedings.length;

      // Find if this belongs to an existing cluster (within 2 hours)
      let addedToCluster = false;
      for (let cluster of clusters) {
        const timeDiff = Math.abs(cluster.startTimeInMinutes - avgStartMinutes);
        if (timeDiff <= 120) {
          // Within 2 hours
          // Merge into existing cluster
          cluster.feedings.push(...feedings);
          cluster.count += feedings.length;

          // Recalculate cluster averages
          cluster.startTimeInMinutes =
            cluster.feedings.reduce((sum, s) => sum + s.startTimeInMinutes, 0) /
            cluster.count;

          addedToCluster = true;
          break;
        }
      }

      if (!addedToCluster) {
        clusters.push({
          startTimeInMinutes: avgStartMinutes,
          count: feedings.length,
          feedings: feedings,
        });
      }
    });

    // Convert clusters to display format and filter by minimum count
    return clusters
      .filter((cluster) => cluster.count >= 5)
      .map((cluster) => {
        const totalStartMinutes = Math.round(cluster.startTimeInMinutes);
        const startHour = Math.floor(totalStartMinutes / 60);
        const startMinute = totalStartMinutes % 60;

        const formatTime = (h, m) => {
          const hour = h % 24;
          return `${hour.toString().padStart(2, "0")}:${m
            .toString()
            .padStart(2, "0")}`;
        };

        return {
          startTime: formatTime(startHour, startMinute),
          startTimeInMinutes: cluster.startTimeInMinutes,
          count: cluster.count,
        };
      })
      .sort((a, b) => a.startTimeInMinutes - b.startTimeInMinutes);
  }, [feedingsData, dateRange]);

  // Calculate today's feeding sessions
  const todayFeedings = useMemo(() => {
    if (!feedingsData || feedingsData.length === 0) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayISO = today.toISOString();

    return feedingsData
      .filter((feeding) => {
        if (!feeding.end) return false; // Skip active/incomplete feedings (handled separately)
        return feeding.start >= todayISO;
      })
      .map((feeding) => {
        const startDate = new Date(feeding.start);
        const startTimeInMinutes =
          startDate.getHours() * 60 + startDate.getMinutes();

        // Check if feeding included bottle milk
        const hasMilk =
          (feeding.extraMilkMM && feeding.extraMilkMM > 0) ||
          (feeding.extraMilkMME && feeding.extraMilkMME > 0);

        return {
          startTimeInMinutes,
          hasMilk,
        };
      });
  }, [feedingsData]);

  // Helper to convert minutes to position on timeline (0-100%)
  const getPosition = (minutes) => {
    return (minutes / 1440) * 100; // 1440 minutes in 24 hours
  };

  // Generate hour markers
  const hourMarkers = [];
  for (let i = 0; i < 24; i++) {
    hourMarkers.push(i);
  }

  if (averageFeedingPatterns.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography
          variant="body1"
          align="center"
          color="text.secondary"
        >
          {t("charts.no_feeding_data")}
        </Typography>
      </Box>
    );
  }

  return (
    <Paper
      elevation={10}
      sx={{
        p: 3,
        bgcolor: "background.card",
        mt: 2,
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="h6">{t("charts.avg_feeding")}</Typography>

        {showDateRangeSelector && (
          <FormControl
            size="small"
            sx={{ minWidth: 120 }}
          >
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              sx={{ backgroundColor: "white" }}
            >
              {dateRangeOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Feeding pattern summary - Two columns */}
      <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
        {/* Average Patterns Column */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            {t("charts.avg_pattern")}
          </Typography>
          {(() => {
            const now = new Date();
            const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();

            return averageFeedingPatterns.map((pattern, index) => (
              <Box
                key={`pattern-${index}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                  opacity:
                    pattern.startTimeInMinutes < currentTimeInMinutes ? 0.4 : 1,
                  position: "relative",
                }}
              >
                <Box
                  sx={{ position: "relative", mr: 1, width: 12, height: 12 }}
                >
                  {index < averageFeedingPatterns.length - 1 && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: "5px",
                        top: "12px",
                        width: "2px",
                        height: "calc(100% + 16px)",
                        backgroundColor: "#E0E0E0",
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: "#AED581",
                      position: "relative",
                      zIndex: 1,
                    }}
                  />
                </Box>
                <Typography variant="body2">
                  {pattern.startTime}{" "}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                  >
                    (avg. {pattern.count}x)
                  </Typography>
                </Typography>
              </Box>
            ));
          })()}
        </Box>

        {/* Today's Feedings Column */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            {t("charts.today")}
          </Typography>
          {(() => {
            const now = new Date();
            const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();

            // Create combined list for today
            const todayItems = [];

            // Add active feeding
            if (activeFeeding) {
              const startDate = new Date(activeFeeding.start);
              const hour = startDate.getHours();
              const minute = startDate.getMinutes();
              const timeInMinutes = hour * 60 + minute;

              // Calculate duration
              const durationMs = now.getTime() - startDate.getTime();
              const totalMinutes = Math.floor(durationMs / 60000);
              const durationHours = Math.floor(totalMinutes / 60);
              const durationMins = totalMinutes % 60;
              const durationText =
                durationHours > 0
                  ? `${durationHours}h ${durationMins}m`
                  : `${durationMins}m`;

              todayItems.push({
                type: "active",
                timeInMinutes,
                time: `${hour.toString().padStart(2, "0")}:${minute
                  .toString()
                  .padStart(2, "0")}`,
                duration: durationText,
              });
            }

            // Add today's feedings
            todayFeedings.forEach((feeding) => {
              const totalMinutes = Math.round(feeding.startTimeInMinutes);
              const hour = Math.floor(totalMinutes / 60);
              const minute = totalMinutes % 60;
              todayItems.push({
                type: "today",
                timeInMinutes: feeding.startTimeInMinutes,
                time: `${hour.toString().padStart(2, "0")}:${minute
                  .toString()
                  .padStart(2, "0")}`,
                hasMilk: feeding.hasMilk,
                isPast: feeding.startTimeInMinutes < currentTimeInMinutes,
              });
            });

            // Sort by time
            todayItems.sort((a, b) => a.timeInMinutes - b.timeInMinutes);

            return todayItems.map((item, index) => {
              if (item.type === "active") {
                return (
                  <Box
                    key={`active-${index}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        mr: 1,
                        width: 12,
                        height: 12,
                      }}
                    >
                      {index < todayItems.length - 1 && (
                        <Box
                          sx={{
                            position: "absolute",
                            left: "5px",
                            top: "12px",
                            width: "2px",
                            height: "calc(100% + 16px)",
                            backgroundColor: "#E0E0E0",
                          }}
                        />
                      )}
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: "#FFB74D",
                          position: "relative",
                          zIndex: 1,
                          animation: "pulse 2s ease-in-out infinite",
                          "@keyframes pulse": {
                            "0%, 100%": {
                              transform: "scale(1)",
                              boxShadow: "0 0 0 0 rgba(245, 124, 0, 0.7)",
                            },
                            "50%": {
                              transform: "scale(1.1)",
                              boxShadow: "0 0 0 6px rgba(245, 124, 0, 0)",
                            },
                          },
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#F57C00",
                        fontWeight: "bold",
                      }}
                    >
                      {item.time}{" "}
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ color: "#F57C00" }}
                      >
                        ({item.duration})
                      </Typography>
                    </Typography>
                  </Box>
                );
              } else {
                return (
                  <Box
                    key={`today-${index}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        mr: 1,
                        width: 12,
                        height: 12,
                      }}
                    >
                      {index < todayItems.length - 1 && (
                        <Box
                          sx={{
                            position: "absolute",
                            left: "5px",
                            top: "12px",
                            width: "2px",
                            height: "calc(100% + 16px)",
                            backgroundColor: "#E0E0E0",
                          }}
                        />
                      )}
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "#66BB6A",
                          border: "2px solid #43A047",
                          position: "relative",
                          zIndex: 1,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: "#43A047" }}
                    >
                      {item.time} {item.hasMilk ? "🍼 " : ""}
                    </Typography>
                  </Box>
                );
              }
            });
          })()}
        </Box>
      </Box>

      {/* Visual timeline */}
      <Box sx={{ position: "relative", height: 100, mt: 4 }}>
        {/* Hour markers */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "100%",
            display: "flex",
          }}
        >
          {hourMarkers.map((hour) => (
            <Box
              key={hour}
              sx={{
                flex: 1,
                borderLeft:
                  hour % 6 === 0 ? "2px solid #ddd" : "1px solid #eee",
                position: "relative",
              }}
            >
              {hour % 3 === 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    position: "absolute",
                    top: -20,
                    left: -8,
                    fontSize: "0.7rem",
                    color: "text.secondary",
                  }}
                >
                  {hour.toString().padStart(2, "0")}
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {/* Feeding indicators */}
        {averageFeedingPatterns.map((pattern, index) => {
          const startPos = getPosition(pattern.startTimeInMinutes);

          return (
            <Box
              key={index}
              sx={{
                position: "absolute",
                left: `${startPos}%`,
                width: 4,
                height: 40,
                top: 30,
                backgroundColor: "#AED581",
                borderRadius: "2px",
                opacity: 0.8,
                transition: "opacity 0.2s",
                "&:hover": {
                  opacity: 1,
                },
              }}
            />
          );
        })}

        {/* Today's feeding sessions */}
        {todayFeedings.map((feeding, index) => {
          const startPos = getPosition(feeding.startTimeInMinutes);

          return (
            <Box
              key={`today-${index}`}
              sx={{
                position: "absolute",
                left: `${startPos}%`,
                width: 4,
                height: 40,
                top: 30,
                backgroundColor: "#66BB6A",
                borderRadius: "2px",
                opacity: 0.9,
                border: "1px solid #43A047",
                zIndex: 3,
              }}
            />
          );
        })}

        {/* Active feeding indicator */}
        {activeFeeding &&
          (() => {
            const startDate = new Date(activeFeeding.start);
            const startTimeInMinutes =
              startDate.getHours() * 60 + startDate.getMinutes();
            const startPos = getPosition(startTimeInMinutes);

            return (
              <Box
                sx={{
                  position: "absolute",
                  left: `${startPos}%`,
                  width: 6,
                  height: 40,
                  top: 30,
                  backgroundColor: "#FFB74D",
                  borderRadius: "3px",
                  opacity: 1,
                  border: "2px solid #F57C00",
                  zIndex: 5,
                  animation: "pulse-bar 2s ease-in-out infinite",
                  "@keyframes pulse-bar": {
                    "0%, 100%": {
                      transform: "scale(1)",
                      boxShadow: "0 0 0 0 rgba(245, 124, 0, 0.7)",
                    },
                    "50%": {
                      transform: "scale(1.05)",
                      boxShadow: "0 0 10px 2px rgba(245, 124, 0, 0.4)",
                    },
                  },
                }}
              />
            );
          })()}

        {/* Current time indicator */}
        <Box
          sx={{
            position: "absolute",
            left: `${getPosition(
              new Date().getHours() * 60 + new Date().getMinutes(),
            )}%`,
            top: 25,
            bottom: 0,
            width: 2,
            backgroundColor: "#F57C00",
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -8,
              left: -4,
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#F57C00",
            }}
          />
        </Box>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 3, textAlign: "center" }}
      >
        {t("charts.based_on_feeding", {
          period:
            dateRangeOptions
              .find((opt) => opt.value === dateRange)
              ?.label.toLowerCase() || t("charts.last_months"),
        })}
      </Typography>
    </Paper>
  );
};

export default FeedingTimeline;
